import React, { useContext, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import analytics from 'client/analytics';
import log from 'client/lib/log';
import ReactAppContext from 'client/core/ReactAppContext';
import configureDecoratedComponent from 'client/helpers/configureDecoratedComponent';

import useIsMounted from 'client/hooks/useIsMounted';

/**
 * Changes the page title HTML tag.
 *
 * @param {string} title The new title to set.
 */
function setPageTitle(title) {
  global.document.querySelector('head title').innerHTML = title;
}

/**
 * Which transition currently owns the global flag, per tree.
 *
 * A WeakMap rather than a module-level variable so trees stay isolated from each other (tests
 * build their own; the server never shares one across requests).
 */
const transitionOwners = new WeakMap();
let transitionSequence = 0;

/**
 * Opens a transition and returns the token that owns it.
 *
 * `tree.commit()` is overridden (see `client/lib/tree.js`) to do nothing while
 * `isTransitioning` is set — it only records `pendingCommit`. So while a transition is open
 * the whole app is held: watchers do not fire and nothing re-renders from state.
 */
export function beginTransition(tree) {
  const token = { id: (transitionSequence += 1) };

  transitionOwners.set(tree, token);
  tree.set('isTransitioning', true);

  return token;
}

/**
 * Closes the transition `token` opened and flushes whatever was held back.
 *
 * Ownership matters because the flag is global while the work that ends it is per-page and
 * asynchronous. Route A can unmount (cleanup closes A's transition), route B can open its own,
 * and only then does A's still-pending `fetchData` resolve. Letting that stale completion run
 * would clear B's flag and flush B's half-loaded state mid-load — the more so during exactly
 * the fast redirects this hook has to survive. A stale token is therefore ignored.
 *
 * Called with no token it closes whatever is open; that is for callers outside the hook's
 * lifecycle, which have no token to present.
 */
export function endTransition(tree, token) {
  if (token && transitionOwners.get(tree) !== token) {
    return false;
  }

  transitionOwners.delete(tree);
  tree.set('isTransitioning', false);

  if (tree.get('pendingCommit')) {
    tree.set('pendingCommit', false);
  }

  tree.commit();

  return true;
}

export default function withTransitionHook(ComponentToDecorate) {
  function DecoratedComponent(props) {
    const reactAppContext = useContext(ReactAppContext);
    const { tree, apiClient, utilityApiClient } = reactAppContext;
    const isMounted = useIsMounted();
    const [isTransitioning, setIsTransitioning] = useState(true);
    const params = useParams();
    const location = useLocation();

    useEffect(() => {
      setIsTransitioning(true);
      // Everything below closes the transition with this token, so a completion that arrives
      // after this page has been replaced cannot end the next page's transition.
      const token = beginTransition(tree);

      Promise.resolve()
        .then(() => {
          if (ComponentToDecorate.fetchData) {
            return ComponentToDecorate.fetchData(
              { params },
              {
                state: tree,
                apiClient: apiClient,
                utilityApiClient: utilityApiClient,
                location: {
                  search: location.search,
                },
              },
            );
          }
        })
        .then(() => {
          // Page metadata and the title only make sense while this page is still on screen,
          // but the transition itself must be ended either way — see `endTransition`.
          if (isMounted()) {
            if (ComponentToDecorate.getPageMetadata) {
              const pageMetadata = ComponentToDecorate.getPageMetadata(tree, { params });
              if (typeof pageMetadata === 'object') {
                tree.set('pageMetadata', pageMetadata);
                tree.set('pendingCommit', true);
              }
            }
            const pageTitle = tree.get(['pageMetadata', 'pageTitle']) || 'Sunroom Rentals';
            setPageTitle(pageTitle);
          }

          endTransition(tree, token);

          if (!isMounted()) {
            return;
          }

          setIsTransitioning(false);
          analytics.pageview();
        })
        .catch((error) => {
          log.error(error);
          endTransition(tree, token);
          setIsTransitioning(false);
        });

      return () => {
        setIsTransitioning(false);
        // Unmounting mid-transition (a page that redirects straight away, or a fast route
        // change) would otherwise strand the flag and freeze every later commit. The next
        // page's effect runs after this cleanup and opens its own transition.
        endTransition(tree, token);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return <ComponentToDecorate {...props} isTransitioning={isTransitioning} />;
  }

  configureDecoratedComponent({
    DecoratedComponent: DecoratedComponent,
    OriginalComponent: ComponentToDecorate,
    displayNameSuffix: 'WithTransitionHook',
  });

  return DecoratedComponent;
}
